import styled from "styled-components";
import { Button } from "src/components/Button";

const hexToRgb = (hex: string) => {
  const sanitized = hex.replace("#", "");
  const bigint = parseInt(sanitized, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `${r}, ${g}, ${b}`;
};

export const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding-top: 1rem;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

export const PaginationButton = styled(Button).attrs(() => ({
  className: "noHover",
}))<{
  $isActive?: boolean;
  $isArrow?: boolean;
  $isDisabled?: boolean;
}>`
  && {
    all: unset;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;

    min-width: 40px;
    height: 40px;
    font-size: 14px;
    font-weight: 700;
    border-radius: ${({ $isArrow }) => ($isArrow ? "10px" : "0.375rem")};
    cursor: ${({ $isDisabled }) => ($isDisabled ? "not-allowed" : "pointer")};
    opacity: ${({ $isDisabled }) => ($isDisabled ? 0.5 : 1)};
    background-color: ${({ theme, $isActive }) =>
      $isActive ? theme.lightGrey : "transparent"};
    color: ${({ theme, $isArrow, $isDisabled }) =>
      $isArrow
        ? $isDisabled
          ? `rgba(${hexToRgb(theme.midGrey)}, 0.7)`
          : theme.midGrey
        : theme.dark};
    border: ${({ $isArrow, $isDisabled, theme }) =>
      $isArrow
        ? $isDisabled
          ? `1px solid rgba(${hexToRgb(theme.grey)}, 0.7)`
          : `1px solid ${theme.grey}`
        : "none"};
    /* create spacing only next to the arrows */
    ${({ $isArrow }) =>
      $isArrow &&
      `
      /* left arrow: first PaginationButton among siblings */
      &:first-of-type { margin-right: 1rem; }
      /* right arrow: last PaginationButton among siblings */
      &:last-of-type { margin-left: 1rem; }
    `}

    svg {
      width: 1rem;
      height: 1rem;
      display: block;
    }
    svg path {
      fill: currentColor !important;
    }

    &:hover {
      background-color: ${({ theme, $isActive }) =>
        $isActive ? theme.lightGrey : "transparent"} !important;
    }
    @media (max-width: 768px) {
      min-width: 32px;
      height: 32px;
      font-size: 12px;

      svg {
        width: 0.875rem;
        height: 0.875rem;
      }
    }
  }
`;

export const EllipsisSpan = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  font-weight: 500;
  font-size: ${({ theme }) => theme.typography.caption};
  color: ${({ theme }) => theme.disabled};
  cursor: pointer;
  position: relative;

  .dropdown-arrow {
    width: 1.25rem; /* ⬅ increase size */
    height: 1.25rem;
    color: ${({ theme }) => theme.midGrey}; /* match arrow buttons */

    path {
      fill: currentColor !important;
    }
  }
`;

export const EllipsisDropdown = styled.div<{ $count: number }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.background};
  border: 1px solid ${({ theme }) => theme.grey};
  border-radius: 4px;
  padding: 0.5rem;
  z-index: 100;

  ${({ $count }) =>
    $count <= 5
      ? `
        display: flex;
        gap: 0.25rem;
        max-width: fit-content;
      `
      : `
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(2rem, 1fr));
        gap: 0.25rem;
        max-width: 12rem;
        max-height: calc(3 * 2.5rem + 2 * 0.25rem); /* 3 rows * item height + gaps */
        overflow-y: auto;
      `}

  /* 👇 Arrow */
  &::before {
    content: "";
    position: absolute;
    top: -6px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid ${({ theme }) => theme.grey};
  }

  &::after {
    content: "";
    position: absolute;
    top: -5px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 5px solid ${({ theme }) => theme.background || "white"};
  }
`;

export const EllipsisItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  cursor: pointer;
  border-radius: 4px;
  color: ${({ theme }) => theme.dark};
  background: ${({ theme }) => theme.background || "white"};
  font-size: 14px;
  font-weight: 700;

  &:hover {
    background: ${({ theme }) => theme.lightGrey || "#eee"};
  }
  @media (max-width: 768px) {
    font-size: 12px !important;
  }
`;
